import { RegularMetricId } from '../../../api/types-graphql';
import {
  BenchmarkPeriods,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { KpiFactboxTwoMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-two-metric/types';
import { OverTimeUnsegmentedVizViews } from '../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { TimePeriodSegmentedVizViews } from '../../../common/components/data-viz-containers/time-period-segmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../constants';
import {
  DataTypes,
  EmployeeDataFields,
  TimeSelectionType,
} from '../../../types';
import { StoryboardDateUtils } from '../utils';

export const getIndividualsFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '4',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Individuals',
          [Languages.JA]: '従業員',
        },
        footer: {
          [Languages.EN]: '1-footer',
          [Languages.JA]: '※対象期間末時点の総数',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getGenderRatioFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '2',
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Gender Ratio', // TODO
          [Languages.JA]: '男女比',
        },
        subtitle: {
          [Languages.EN]: 'Cumulative total at the end of the period', // TODO
          [Languages.JA]: '（女：男）', //  this is different from the eng subtitle
        },
        footer: {
          [Languages.EN]: '2-footer', // TODO
          [Languages.JA]: '※対象期間末時点における累計',
        },
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: 'Female',
              [Languages.JA]: '女性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0020_2FhcPercHc,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: {
                  start: nowEndOfMonth,
                  end: nowEndOfMonth,
                },
              },
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: 'Male',
              [Languages.JA]: '男性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0448_2MhcPerPerson,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: {
                  start: nowEndOfMonth,
                  end: nowEndOfMonth,
                },
              },
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
    layoutConfig: {
      gridColSpan: 1,
    },
  };
};

export const getAgeFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '3',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '3-title', // TODO
          [Languages.JA]: '年齢',
        },
        subtitle: {
          [Languages.EN]: '3-subtitle', // TODO
          [Languages.JA]: '（平均）',
        },
        footer: {
          [Languages.EN]: '3-footer', // TODO
          [Languages.JA]: '※対象期間末時点の平均 ',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0019_2AgeAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getTenureFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '4',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '4-title', // TODO
          [Languages.JA]: '勤続年数',
        },
        subtitle: {
          [Languages.EN]: '4-subtitle', // TODO
          [Languages.JA]: '（平均）',
        },
        footer: {
          [Languages.EN]: '4-footer', // TODO
          [Languages.JA]: '※対象期間末時点の平均 ',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0023_2TenureAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getManagementRatioFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '5',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '5-title', // TODO
          [Languages.JA]: '管理職比率',
        },
        footer: {
          [Languages.EN]: '5-footer', // TODO
          [Languages.JA]: '※対象期間末時点の比率',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0014_2MngDefPercHc,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getJoinersFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '6',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '6-title', // TODO
          [Languages.JA]: '入社者',
        },
        footer: {
          [Languages.EN]: '6-footer', // TODO
          [Languages.JA]: '※対象期間における累計 ',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0006_1JoinersJoiners,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getLeaversFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '7',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '7-title', // TODO
          [Languages.JA]: '離職者',
        },
        footer: {
          [Languages.EN]: '7-footer', // TODO
          [Languages.JA]: '※対象期間における累計',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getAttritionFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '8',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '8-title', // TODO
          [Languages.JA]: '離職率',
        },
        footer: {
          [Languages.EN]: '8-footer', // TODO
          [Languages.JA]:
            '※計算式：選択した月から 直近12ヶ月間の月間離職率の合計',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0076_3AttritionTtm,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getFirstYearAttritionFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: '9',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '9-title', // TODO
          [Languages.JA]: '入社１年以内離職率',
        },
        footer: {
          [Languages.EN]: '9-footer', // TODO
          [Languages.JA]:
            '※計算式：選択した月から 直近12ヶ月間の月間入社１年以内離職率の合計',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0290_1_1yAttritionAttrition,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getParentalLeaveFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowStartOfMonth } = dateUtils;
  return {
    chartId: '10',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '10-title', // TODO
          [Languages.JA]: '育休取得率',
        },
        footer: {
          [Languages.EN]: '10-footer', // TODO
          [Languages.JA]:
            '※計算式：期間内に育休を開始した人数÷同期間に子が誕生した人数',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0171ParentalLeaveTakenUtilizationPerc,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: twelveMonthsAgoFromNowStartOfMonth,
                end: nowEndOfMonth,
              },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

// Demographics page charts

interface DemographicsChartParams {
  chartId: string;
  title: {
    [Languages.EN]: string;
    [Languages.JA]: string;
  };
  groupByDataField: EmployeeDataFields;
  gridColSpan: number;
}

const createDemographicsChart = (
  dateUtils: StoryboardDateUtils,
  params: DemographicsChartParams
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: params.chartId,
    layoutConfig: {
      gridColSpan: params.gridColSpan,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
      dataConfig: {
        title: params.title,
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
          timeSelection: {
            type: TimeSelectionType.CalendarYearSingleValueByMonths,
            input: {
              start: nowEndOfMonth,
              end: nowEndOfMonth,
            },
          },
          groupBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: params.groupByDataField,
            },
          },
          segmentBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.GENDER,
            },
          },
        },
      },
      selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
    },
  };
};

export const getDemographicsCustomFilterChart = (
  dateUtils: StoryboardDateUtils,
  gridColSpan: number
): StoryboardItemConfig => {
  return createDemographicsChart(dateUtils, {
    chartId: '1',
    title: {
      [Languages.EN]: '', // TODO
      [Languages.JA]: '雇用形態別',
    },
    groupByDataField: EmployeeDataFields.CUSTOM_FILTER_4,
    gridColSpan,
  });
};

export const getDemographicsAgeChart = (
  dateUtils: StoryboardDateUtils,
  gridColSpan: number
): StoryboardItemConfig => {
  return createDemographicsChart(dateUtils, {
    chartId: '2',
    title: {
      [Languages.EN]: '', // TODO
      [Languages.JA]: '年齢構成',
    },
    groupByDataField: EmployeeDataFields.AGE_GROUP,
    gridColSpan,
  });
};

export const getDemographicsTenureChart = (
  dateUtils: StoryboardDateUtils,
  gridColSpan: number
): StoryboardItemConfig => {
  return createDemographicsChart(dateUtils, {
    chartId: '3',
    title: {
      [Languages.EN]: '', // TODO
      [Languages.JA]: '勤続年数グループ別',
    },
    groupByDataField: EmployeeDataFields.TENURE_GROUP,
    gridColSpan,
  });
};

export const getDemographicsFunctionChart = (
  dateUtils: StoryboardDateUtils,
  gridColSpan: number
): StoryboardItemConfig => {
  return createDemographicsChart(dateUtils, {
    chartId: '4',
    title: {
      [Languages.EN]: '', // TODO
      [Languages.JA]: '職種別',
    },
    groupByDataField: EmployeeDataFields.FUNCTION_LEVEL_1,
    gridColSpan,
  });
};

export const getDemographicsPositionChart = (
  dateUtils: StoryboardDateUtils,
  gridColSpan: number
): StoryboardItemConfig => {
  return createDemographicsChart(dateUtils, {
    chartId: '5',
    title: {
      [Languages.EN]: '', // TODO
      [Languages.JA]: '役職別',
    },
    groupByDataField: EmployeeDataFields.POSITION_LEVEL_1,
    gridColSpan,
  });
};

export const getDemographicsJobGradeChart = (
  dateUtils: StoryboardDateUtils,
  gridColSpan: number
): StoryboardItemConfig => {
  return createDemographicsChart(dateUtils, {
    chartId: '6',
    title: {
      [Languages.EN]: '', // TODO
      [Languages.JA]: '職務等級別',
    },
    groupByDataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
    gridColSpan,
  });
};

// Joiners and Leavers page charts

export const getJoinersLeaversOverTimeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: '1',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '', // TODO
          [Languages.JA]: '入社者数・離職者数の推移（直近12ヶ月）',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0006_1JoinersJoiners,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getJoinersLeaversByAgeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: '2',
    layoutConfig: {
      gridColSpan: 3,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '', // TODO
          [Languages.JA]: '年代別、入社者数と離職者数の比較',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0006_1JoinersJoiners,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.AGE_GROUP,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getJoinersOverTimeWithBenchmarkChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: '3',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '', // TODO
          [Languages.JA]: '入社者数の前年同月比（直近12ヶ月）',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0006_1JoinersJoiners,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        benchmarkConfig: {
          benchmarkPeriod: BenchmarkPeriods.YEAR_ON_YEAR,
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getLeaversOverTimeWithBenchmarkChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: '4',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '', // TODO
          [Languages.JA]: '離職者数の前年同月比（直近12ヶ月）',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        benchmarkConfig: {
          benchmarkPeriod: BenchmarkPeriods.YEAR_ON_YEAR,
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};
